import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  TrendingUp,
  Business,
  Assignment,
  ShoppingCart,
  Stop as StopIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const renderBoldText = (text: string) => {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
};

const renderMessageContent = (content: string) => {
  // Strip markdown headings (###, ##, #) and leading ? from lines
  const cleaned = content.split('\n').map(line => {
    const headingMatch = line.match(/^#{1,3}\s+(.*)/);
    if (headingMatch) return headingMatch[1];
    return line;
  }).join('\n');
  const lines = cleaned.split('\n');
  const segments: { type: 'text' | 'table'; lines: string[] }[] = [];
  let current: { type: 'text' | 'table'; lines: string[] } | null = null;

  for (const line of lines) {
    const isTableLine = line.trim().startsWith('|');
    if (isTableLine) {
      if (!current || current.type !== 'table') {
        if (current) segments.push(current);
        current = { type: 'table', lines: [] };
      }
      current.lines.push(line);
    } else {
      if (!current || current.type !== 'text') {
        if (current) segments.push(current);
        current = { type: 'text', lines: [] };
      }
      current.lines.push(line);
    }
  }
  if (current) segments.push(current);

  return segments.map((seg, idx) => {
    if (seg.type === 'text') {
      const text = seg.lines.join('\n').trim();
      if (!text) return null;
      return (
        <Typography key={idx} variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 1, fontFamily: 'inherit' }}>
          {text.split('\n').map((line, li) => (
            <span key={li}>{renderBoldText(line)}{li < text.split('\n').length - 1 ? '\n' : ''}</span>
          ))}
        </Typography>
      );
    }

    const parseRow = (line: string) => line.split('|').slice(1, -1).map(c => c.trim());
    const nonEmpty = seg.lines.filter(l => l.trim());
    if (nonEmpty.length < 2) return null;
    const headers = parseRow(nonEmpty[0]);
    const rows = nonEmpty.slice(2).map(parseRow).filter(r => r.some(c => c));

    return (
      <Box key={idx} sx={{ overflowX: 'auto', my: 1.5 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem' }}>
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i} style={{
                  padding: '8px 16px',
                  textAlign: 'left',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  borderBottom: '2px solid #d0d0d0',
                  whiteSpace: 'nowrap',
                  fontFamily: 'inherit',
                  backgroundColor: 'transparent'
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} style={{
                    padding: '7px 16px',
                    color: '#333',
                    borderBottom: '1px solid #efefef',
                    whiteSpace: 'nowrap',
                    fontFamily: 'inherit'
                  }}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    );
  });
};

interface QuickAction {
  icon: React.ReactNode;
  title: string;
  description: string;
  query: string;
}

interface Conversation {
  conversation_id: number;
  title: string | null;
  message_count: number;
  created_at: string;
  updated_at: string;
}

const WinstonChat = ({ projectId, access }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<number | null>(null);
  const [creatingNewChat, setCreatingNewChat] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const quickActions: QuickAction[] = [
    {
      icon: <TrendingUp sx={{ fontSize: 28, color: '#1976d2' }} />,
      title: 'User Statistics',
      description: 'Get information about users',
      query: 'How many users are there?'
    },
    {
      icon: <Business sx={{ fontSize: 28, color: '#1976d2' }} />,
      title: 'Companies',
      description: 'View company information',
      query: 'List all companies name'
    },
    {
      icon: <Assignment sx={{ fontSize: 28, color: '#1976d2' }} />,
      title: 'Active Projects',
      description: 'Check project status',
      query: 'Show me all active projects name'
    },
    {
      icon: <ShoppingCart sx={{ fontSize: 28, color: '#1976d2' }} />,
      title: 'Purchase Orders',
      description: 'View PO information',
      query: 'What are the pending purchase orders?'
    }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversations list when component mounts
  useEffect(() => {
    loadConversations();
  }, [projectId]);

  const loadConversations = async () => {
    try {
      setLoadingConversations(true);
      const queryParams = projectId ? `?project_id=${projectId}` : '';
      const response = await fetch(apiUrl(`/api/ai/chat/conversations${queryParams}`), {
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success && data.conversations) {
        setConversations(data.conversations);

        // If there are conversations, load the most recent one
        if (data.conversations.length > 0 && !currentConversationId) {
          loadConversation(data.conversations[0].conversation_id);
        }
      }
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversation = async (conversationId: number) => {
    try {
      const response = await fetch(apiUrl(`/api/ai/chat/conversations/${conversationId}`), {
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success && data.conversation && data.conversation.messages) {
        const historyMessages = data.conversation.messages.map((msg: any) => ({
          role: msg.role,
          content: msg.content
        }));
        setMessages(historyMessages);
        setCurrentConversationId(conversationId);
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  const createNewChat = async () => {
    try {
      setCreatingNewChat(true);
      const response = await fetch(apiUrl('/api/ai/chat/conversations'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          project_id: projectId
        })
      });

      const data = await response.json();

      if (data.success && data.conversation) {
        // Clear current messages
        setMessages([]);
        setCurrentConversationId(data.conversation.conversation_id);
        // Reload conversations list
        await loadConversations();
        setSnackbarMessage('New chat created successfully');
      }
    } catch (err) {
      console.error('Failed to create new chat:', err);
      setSnackbarMessage('Failed to create new chat');
    } finally {
      setCreatingNewChat(false);
    }
  };

  const deleteConversation = (conversationId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setConversationToDelete(conversationId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;

    try {
      const response = await fetch(apiUrl(`/api/ai/chat/conversations/${conversationToDelete}`), {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        // If deleted conversation was current one, clear messages
        if (conversationToDelete === currentConversationId) {
          setMessages([]);
          setCurrentConversationId(null);
        }
        // Reload conversations list
        await loadConversations();
        setSnackbarMessage('Conversation deleted successfully');
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      setSnackbarMessage('Failed to delete conversation');
    } finally {
      setDeleteDialogOpen(false);
      setConversationToDelete(null);
    }
  };

  const deleteAllConversations = () => {
    if (conversations.length === 0) return;
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllConversations = async () => {
    const count = conversations.length;

    try {
      const response = await fetch(apiUrl('/api/ai/chat/history'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          project_id: projectId
        })
      });

      const data = await response.json();

      if (data.success) {
        // Clear current messages and conversation
        setMessages([]);
        setCurrentConversationId(null);
        // Reload conversations list (should be empty now)
        await loadConversations();
        setSnackbarMessage(`${count} conversation${count !== 1 ? 's' : ''} deleted successfully`);
      }
    } catch (err) {
      console.error('Failed to delete all conversations:', err);
      setSnackbarMessage('Failed to delete conversations');
    } finally {
      setDeleteAllDialogOpen(false);
    }
  };

  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || loading) return;

    const userMessage: Message = {
      role: 'user',
      content: textToSend
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setError(null);

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      console.log('🔍 WinstonChat sending project_id:', projectId, 'conversation_id:', currentConversationId);
      const response = await fetch(apiUrl('/api/ai/chat'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          messages: [...messages, userMessage],
          project_id: projectId,  // Send project ID for document search
          conversation_id: currentConversationId  // Send conversation ID to save to specific conversation
        }),
        signal: abortControllerRef.current.signal
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to get response');
      }

      if (data.success && data.response) {
        const assistantMessage: Message = {
          role: 'assistant',
          content: data.response
        };
        setMessages(prev => [...prev, assistantMessage]);

        // Update current conversation ID if it was returned (for new conversations)
        if (data.conversation_id && !currentConversationId) {
          setCurrentConversationId(data.conversation_id);
        }

        // Reload conversations list to update titles and message counts
        loadConversations();
      } else {
        throw new Error(data.error || 'No response from AI');
      }

    } catch (err: any) {
      // Check if request was aborted
      if (err.name === 'AbortError') {
        console.log('Request was cancelled by user');
        const cancelledMessage: Message = {
          role: 'assistant',
          content: 'Request cancelled by user.'
        };
        setMessages(prev => [...prev, cancelledMessage]);
        return;
      }

      console.error('Chat error:', err);
      setError(err.message || 'Failed to send message');

      const errorMessage: Message = {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${err.message}. Please try again or contact support.`
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  const stopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickAction = (query: string) => {
    sendMessage(query);
  };

  if (access === 'none') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Access denied. You don't have permission to use the AI Assistant.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 56px - 40px)', overflow: 'hidden' }}>
      {/* Sidebar - Conversations List */}
      <Box
        sx={{
          width: sidebarOpen ? 280 : 0,
          transition: 'width 0.3s',
          borderRight: sidebarOpen ? '1px solid #e0e0e0' : 'none',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f8f9fa'
        }}
      >
        {/* Sidebar Header */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle2" fontWeight="600">
              Chat History
            </Typography>
            {conversations.length > 0 && (
              <IconButton
                size="small"
                onClick={deleteAllConversations}
                sx={{
                  color: 'error.main',
                  opacity: 0.7,
                  '&:hover': { opacity: 1, bgcolor: 'error.lighter' }
                }}
                title="Delete all conversations"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {/* Conversations List */}
        <List sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          {loadingConversations ? (
            // Loading state
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : conversations.length === 0 ? (
            // Empty state
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No conversations yet
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Start a new chat to begin
              </Typography>
            </Box>
          ) : (
            // Conversations list
            conversations.map((conv) => (
              <ListItem
                key={conv.conversation_id}
                disablePadding
                sx={{ mb: 0.5 }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => deleteConversation(conv.conversation_id, e)}
                    sx={{ opacity: 0.6, '&:hover': { opacity: 1, color: 'error.main' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemButton
                  selected={conv.conversation_id === currentConversationId}
                  onClick={() => loadConversation(conv.conversation_id)}
                  sx={{
                    borderRadius: 1,
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'primary.dark'
                      }
                    }
                  }}
                >
                  <ListItemText
                    primary={conv.title || 'New conversation'}
                    secondary={`${conv.message_count} messages`}
                    slotProps={{
                      primary: {
                        noWrap: true,
                        fontSize: '0.9rem',
                        color: conv.conversation_id === currentConversationId ? 'inherit' : 'text.primary'
                      },
                      secondary: {
                        fontSize: '0.75rem',
                        color: conv.conversation_id === currentConversationId ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>
      </Box>

      {/* Main Chat Area */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', p: 2 }}>
        {/* Header */}
        <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={() => setSidebarOpen(!sidebarOpen)}
                sx={{ mr: 1 }}
              >
                {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <BotIcon sx={{ fontSize: 28, color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="600">
                  AI Assistant
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ask me anything about your application data
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="contained"
                startIcon={creatingNewChat ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                onClick={createNewChat}
                disabled={creatingNewChat}
                sx={{ textTransform: 'none' }}
              >
                {creatingNewChat ? 'Creating...' : 'New Chat'}
              </Button>
              <Button
                variant="outlined"
                href="/ai-settings"
                sx={{ textTransform: 'none' }}
              >
                ⚙️ Configure
              </Button>
            </Box>
          </Box>
        </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Messages Area */}
      <Box
        ref={scrollerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          bgcolor: '#f8f9fa',
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {messages.length === 0 ? (
          // Welcome Screen
          <Box sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}>
            <Box sx={{ textAlign: 'center', mb: 5, mt: 4 }}>
              <Typography variant="h4" fontWeight="600" gutterBottom color="text.primary" sx={{ mb: 1.5 }}>
                Welcome to AI Assistant
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
                Your intelligent assistant for application data analysis. Ask me about statistics,
                data queries, projects, users, and more.
              </Typography>
            </Box>

            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              {quickActions.map((action, idx) => (
                <Card
                  key={idx}
                  elevation={0}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      borderColor: '#1976d2'
                    }
                  }}
                  onClick={() => handleQuickAction(action.query)}
                >
                  <CardContent sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start', p: 3 }}>
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2,
                        bgcolor: '#f0f7ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      {action.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="600" gutterBottom sx={{ mb: 0.5 }}>
                        {action.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {action.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            <Box
              sx={{
                mt: 4,
                p: 2.5,
                bgcolor: '#f0f7ff',
                borderRadius: 2,
                border: '1px solid #e3f2fd'
              }}
            >
              <Typography variant="body2" color="text.primary" sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <span style={{ fontSize: '18px' }}>💡</span>
                <span>
                  <strong>Tip:</strong> You can ask me questions like "How many users are there?",
                  "Show me pending approvals", or "List all active projects"
                </span>
              </Typography>
            </Box>
          </Box>
        ) : (
          // Chat Messages
          <Box sx={{ width: '100%' }}>
            {messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 2
                }}
              >
                {msg.role === 'assistant' && (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mr: 1.5
                    }}
                  >
                    <BotIcon sx={{ fontSize: 22, color: 'white' }} />
                  </Box>
                )}

                <Paper
                  elevation={msg.role === 'user' ? 0 : 1}
                  sx={{
                    p: 2,
                    maxWidth: msg.role === 'user' ? '70%' : '100%',
                    width: msg.role === 'user' ? 'auto' : 'fit-content',
                    '&:has(.table-scroll-wrapper)': { width: '100%' },
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'white',
                    color: msg.role === 'user' ? 'white' : 'text.primary',
                    borderRadius: 2,
                    wordBreak: 'break-word',
                    '& .table-scroll-wrapper': {
                      overflowX: 'auto',
                      width: '100%',
                      marginTop: 2,
                      marginBottom: 2,
                    },
                    '& table': {
                      borderCollapse: 'collapse',
                      minWidth: '100%',
                      fontSize: '0.9rem'
                    },
                  }}
                >
                  {msg.role === 'user' ? (
                    <Typography variant="body1">{msg.content}</Typography>
                  ) : (
                    <Box>{renderMessageContent(msg.content)}</Box>
                  )}
                </Paper>

                {msg.role === 'user' && (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: 'grey.300',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      ml: 1.5
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 22, color: 'grey.700' }} />
                  </Box>
                )}
              </Box>
            ))}

            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5
                  }}
                >
                  <BotIcon sx={{ fontSize: 22, color: 'white' }} />
                </Box>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                  <CircularProgress size={24} />
                </Paper>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

        {/* Input Area */}
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Ask me anything about your data..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            multiline
            maxRows={3}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'white'
              }
            }}
          />
          {loading ? (
            <Button
              variant="contained"
              color="error"
              onClick={stopGeneration}
              sx={{
                minWidth: 120,
                height: 56,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem'
              }}
              startIcon={<StopIcon />}
            >
              Stop
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => sendMessage()}
              disabled={!input.trim()}
              sx={{
                minWidth: 120,
                height: 56,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem'
              }}
              startIcon={<SendIcon />}
            >
              Send
            </Button>
          )}
        </Box>
      </Box>

      {/* Delete Single Conversation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Conversation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this conversation? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDeleteConversation} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Conversations Dialog */}
      <Dialog
        open={deleteAllDialogOpen}
        onClose={() => setDeleteAllDialogOpen(false)}
      >
        <DialogTitle>Delete All Conversations?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDeleteAllConversations} color="error" variant="contained" autoFocus>
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarMessage(null)}
          severity={snackbarMessage?.includes('Failed') ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WinstonChat;
